import React, { useState, useContext, useEffect } from "react";
import { Box, Card, Grid, List, ListSubheader, } from "@material-ui/core";

import { Entity } from "../../model/Provider";
import { AppContext } from "../../context/AppContext";
import { deleteExtendedField, getProviderExtendedFields, updateExtendedField } from "../../api/ProviderAPI";
import Progress from "../../components/Progress";
import { ExtendedField } from "../../model/ExtendedField";
import { TenantField } from "../../model/TenantFieldCatalog";
import translate from "../../i18n/Translator";
import { ErrorSnackbar, SuccessSnackbar } from "../../components/Snackbars";
import FieldData from "../../components/FieldItems/FieldItemData";
import ExpedientProviderFieldForm from "../../expedients/ExpedientProviderFieldForm";
import { ProviderExpedientField } from '../../model/ProviderExpedient';
import { FieldItemDeletePopup } from "../../components/FieldItems/FieldItemsDetail";


interface ProviderExtendedFieldProps {
    provider: Entity;
}

export default function ProviderExtendedField(props: ProviderExtendedFieldProps) {
    const [loading, setLoading] = useState<boolean>(true);

    const [fields, setFields] = useState<ExtendedField[]>();
    const [field, setField] = useState<ExtendedField>();
    const [fieldToDelete, setFieldToDelete] = useState<ExtendedField>();

    const context = useContext(AppContext);
    const [success, setSuccess] = useState<string>();
    const [error, setError] = useState<string | JSX.Element | JSX.Element[]>();

    const onInit = () => {
        getProviderExtendedFields(context.session!.tenant!.id, props.provider.id).then((r) => {

            setFields(r.extended_fields?.map(f => {
                return {
                    ...f.field,
                    id: f.field.id,
                    name: f.field.name,
                    description: f.field.description,
                    type: f.field.type,
                    value: f.value,
                    editable: true,
                    required: f.field.required,
                    read_only: false,
                    values_catalog: f?.values_catalog,
                    by_each_payment: false,
                    payment_process_history: [],
                    external_id: f.external_id,
                    catalog_id: f.field.catalog_id,
                    aux_file_name: f.field.aux_file_name,
                    aux_file_url: f.field.aux_file_url
                } as ExtendedField;
            }) ?? []);

        }).finally(() => {
            setLoading(false);
        });

    }
    useEffect(onInit, [props!.provider]);

    if (loading) {
        return <Progress />;
    }

    const onEditField = (value: string) => {
        if (field) {
            if ("CATALOG" === field.type && !!value) {
                value = field?.values_catalog.find(e => e.external_id === value)?.id ?? "";
            }
            updateExtendedField(context.session!.tenant!.id, props.provider.id, field!.external_id!, value)
                .then(() => {
                    setSuccess(translate("provider_fields.extended_fiel_update_value_success" as string) as string);

                    setField(undefined);
                    onInit();
                }).catch((error) => {
                    setError(error?.message ?? undefined);
                });
        }
    };

    const onSubmitDelete = (field: ExtendedField) => {
        deleteExtendedField(context.session!.tenant!.id, props.provider.id, field!.external_id!)
            .then(() => {
                setSuccess(translate("provider_fields.extended_fiel_delete_value_success" as string) as string);

                onCloseDeleteField();
                onInit();
            }).catch((error) => {
                setError(error?.message ?? undefined);
            });
    };

    const onDeleteField = (field: ExtendedField) => {
        setFieldToDelete(field);
    }

    const onCloseDeleteField = () => {
        setFieldToDelete(undefined);
    }

    const onClosedSnackbar = () => {
        setError(undefined);
        setSuccess(undefined);
    };

    if (!fields || fields.length === 0) {
        return <></>
    }

    return (
        <Grid item xs={12} style={{ paddingBottom: "20px" }}>
            <Card variant="outlined" >

                <List component="nav" dense subheader={
                    <ListSubheader disableSticky>
                        {translate("provider_fields.extended_fields_title" as string)}
                    </ListSubheader>
                }>
                    <Grid container id="provider_extendes_fields">
                        {fields.map(field => (
                            <Grid item key={field.id} xs={12} md={6}>
                                <Box px={2}>
                                    <Grid container justify="center" alignContent="center" alignItems="center" spacing={2}>
                                        <Grid item xs={4}>
                                            <FieldLabel required={field.required} value={field.name} />
                                        </Grid>
                                        <Grid item xs={8} style={{ overflowWrap: "anywhere" }}>
                                            <FieldData onlyRead={!field.editable}
                                                canDelete={true}
                                                onEditField={(field) => {
                                                    setField(field);
                                                }}
                                                onDeleteField={onDeleteField}
                                                onShowPaymentHistory={() => { }}
                                                field={field}
                                                tenantId={context!.session!.tenant!.id}
                                                cfdiId={""}
                                                ommitDateFormat
                                            />
                                        </Grid>
                                    </Grid>
                                </Box>
                            </Grid>
                        ))}
                    </Grid>

                </List>

            </Card>

            {field &&
                (<ExpedientProviderFieldForm
                    field={{
                        configuration: {
                            ...field,
                            privado: false,
                            use_in_cfdi: false,
                        } as TenantField,
                        history: []
                    } as ProviderExpedientField}
                    title={field.name}
                    subtitle={field.description}
                    value={field.value}
                    valueExternalId={"CATALOG" === field.type ? field?.values_catalog.find(e => e.id === field.value)?.external_id : undefined}
                    comments={field.comments}
                    onClose={() => { setField(undefined) }}
                    onCompleted={onEditField}
                    onDeleted={() => { }}
                    disabled={false}
                    provider={props.provider}
                    getFormmatedDate={(date: Date) => {
                        if (date) {
                            return String(date.getFullYear() + "-" + date.getMonth() + 1).padStart(2, '0') + "-" + String(date.getDate()).padStart(2, '0');
                        }
                        return "";
                    }}
                    dateFormat="YYYY-MM-DD"
                    hideAdornment
                />)}

            {fieldToDelete &&
                <FieldItemDeletePopup fieldToDelete={fieldToDelete} onCloseDeleteField={onCloseDeleteField} onSubmitDelete={onSubmitDelete} />
            }
            <ErrorSnackbar message={error} onClose={onClosedSnackbar} />
            <SuccessSnackbar message={success} onClose={onClosedSnackbar} />

        </Grid>
    );

}

interface FieldLabelProps {
    required: boolean;
    value: string;
}

function FieldLabel(props: FieldLabelProps) {
    return (
        <div>
            {!props.required &&
                props.value
            }
            {props.required &&
                translate("cfdis.template.field_required", { "value": props.value })
            }
        </div>
    );
}